import apiClient from './client';
import type {
  LoginCredentials,
  AuthResponse,
  UserProfile,
  UserProfileUpdate,
  PasswordChangeRequest,
  NotificationPreferences,
  EmailChangeRequest,
  EmailChangeResponse,
  GoogleSignInRequest,
  GoogleSignInResponse,
  GoogleSignInPendingResponse,
  GoogleCompleteSignupRequest,
  GoogleLinkRequest,
  GoogleLinkResponse,
  GoogleUnlinkResponse,
  AuthInfoResponse,
  SetPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  CompanyAddress,
} from '@/types/auth';

export interface GoogleAdsAccount {
  google_ads_account_id: string;
  account_name?: string;
  is_active: boolean;
  linked_at: string;
}

export interface Subscription {
  plan_name: string;
  monthly_price: number;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete';
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
}

export interface Invoice {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: string;
  pdf_url?: string;
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Backend expects form-encoded data, not JSON
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await apiClient.post<AuthResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  async logout(): Promise<void> {
    // Note: Current backend doesn't have a logout endpoint
    // Just clear local session data
    return Promise.resolve();
  },

  async verifyToken(): Promise<boolean> {
    try {
      // Since the verify endpoint doesn't exist, just check if we have a token
      // and try a simple API call to validate it
      const response = await apiClient.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  },

  async getGoogleAdsAccounts(): Promise<GoogleAdsAccount[]> {
    // Let errors propagate - caller should handle API failures
    // Returning empty array would mask actual errors from network/auth issues
    const response = await apiClient.get<GoogleAdsAccount[]>('/auth/google/accounts');
    return response.data;
  },

  // Settings API functions
  async getUserProfile(): Promise<UserProfile> {
    interface UserProfileResponse {
      member_uuid: string;
      email: string;
      first_name?: string;
      last_name?: string;
      company_name?: string;
      company_industry?: string;
      company_address?: CompanyAddress;
      phone?: string;
      website?: string;
      timezone?: string;
      is_active?: boolean;
      is_superuser?: boolean;
    }
    const response = await apiClient.get<UserProfileResponse>('/auth/user/profile');
    // Transform snake_case to camelCase
    return {
      memberUuid: response.data.member_uuid,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      companyName: response.data.company_name,
      companyIndustry: response.data.company_industry,
      companyAddress: response.data.company_address,
      phone: response.data.phone,
      website: response.data.website,
      timezone: response.data.timezone,
      isActive: response.data.is_active,
      isSuperuser: response.data.is_superuser,
    };
  },

  async updateUserProfile(data: UserProfileUpdate): Promise<UserProfile> {
    interface UserProfileResponse {
      member_uuid: string;
      email: string;
      first_name?: string;
      last_name?: string;
      company_name?: string;
      company_industry?: string;
      company_address?: CompanyAddress;
      phone?: string;
      website?: string;
      timezone?: string;
      is_active?: boolean;
      is_superuser?: boolean;
    }
    const response = await apiClient.put<UserProfileResponse>('/auth/user/profile', data);
    // Transform snake_case to camelCase
    return {
      memberUuid: response.data.member_uuid,
      email: response.data.email,
      firstName: response.data.first_name,
      lastName: response.data.last_name,
      companyName: response.data.company_name,
      companyIndustry: response.data.company_industry,
      companyAddress: response.data.company_address,
      phone: response.data.phone,
      website: response.data.website,
      timezone: response.data.timezone,
      isActive: response.data.is_active,
      isSuperuser: response.data.is_superuser,
    };
  },

  async changePassword(data: PasswordChangeRequest): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>('/auth/password', data);
    return response.data;
  },

  async changeEmail(data: EmailChangeRequest): Promise<EmailChangeResponse> {
    const response = await apiClient.put<EmailChangeResponse>('/auth/email', data);
    return response.data;
  },

  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const response = await apiClient.get<NotificationPreferences>('/notifications/preferences');
    return response.data;
  },

  async updateNotificationPreferences(data: NotificationPreferences): Promise<NotificationPreferences> {
    const response = await apiClient.put<NotificationPreferences>('/notifications/preferences', data);
    return response.data;
  },

  // Billing API functions
  async getSubscription(): Promise<Subscription | null> {
    try {
      const response = await apiClient.get<Subscription>('/billing/subscription');
      return response.data;
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number } };
      if (apiError.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async getInvoices(): Promise<Invoice[]> {
    const response = await apiClient.get<Invoice[]>('/billing/invoices');
    return response.data;
  },

  async createBillingPortalSession(): Promise<{ url: string }> {
    const response = await apiClient.post('/billing/portal-session');
    return response.data;
  },

  async cancelSubscription(): Promise<{ message: string; cancel_at: string | null }> {
    const response = await apiClient.post('/billing/subscription/cancel');
    return response.data;
  },

  async resumeSubscription(): Promise<{ message: string }> {
    const response = await apiClient.post('/billing/subscription/resume');
    return response.data;
  },

  // ============================================================================
  // Google Sign-In API Functions
  // ============================================================================

  async googleSignIn(data: GoogleSignInRequest): Promise<GoogleSignInResponse | GoogleSignInPendingResponse> {
    const response = await apiClient.post<GoogleSignInResponse | GoogleSignInPendingResponse>(
      '/auth/google/signin',
      data
    );
    return response.data;
  },

  async googleCompleteSignup(data: GoogleCompleteSignupRequest): Promise<GoogleSignInResponse> {
    const response = await apiClient.post<GoogleSignInResponse>('/auth/google/complete-signup', data);
    return response.data;
  },

  async googleLinkAccount(data: GoogleLinkRequest): Promise<GoogleLinkResponse> {
    const response = await apiClient.post<GoogleLinkResponse>('/auth/google/link', data);
    return response.data;
  },

  async googleUnlinkAccount(): Promise<GoogleUnlinkResponse> {
    const response = await apiClient.delete<GoogleUnlinkResponse>('/auth/google/link');
    return response.data;
  },

  async getAuthInfo(): Promise<AuthInfoResponse> {
    const response = await apiClient.get<AuthInfoResponse>('/auth/auth-info');
    return response.data;
  },

  async setPassword(data: SetPasswordRequest): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/set-password', data);
    return response.data;
  },

  // ============================================================================
  // Password Reset API Functions
  // ============================================================================

  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/forgot-password',
      { email }
    );
    return response.data;
  },

  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await apiClient.post<ResetPasswordResponse>(
      '/auth/reset-password',
      data
    );
    return response.data;
  },
};